import { GeneratedModule } from './GeneratedModule/GeneratedModule';
import { Header } from '@/components/Layout/Header/Header';
import TopicsList from '@/components/Views/Topics/TopicsList';
import FeedbackModal from './FeedbackModal';
import AudioPage from './Audio/AudioPage';
import { Divider } from 'primereact/divider';
import { useState, useMemo, useEffect } from 'react';
import { Summary } from './Summary/Summary';
import { AiLoader } from '@/components/Ui/AiLoader/AiLoader';
import { useSaveGeneratedContent } from '@/api/hooks/useSaveGeneratedContent';
import { useToast } from '@/hooks/useToast';
import { useParams } from 'react-router-dom';
import { useGenerator } from './contentGenerator.hook';
import { useGlobalContextStore } from '@/store/GlobalContextStore/globalContextStore';
import { AlertModal } from '@/components/Layout/AlertModal/AlertModal';
import {
  HighlightOutput,
  GenerateSectionOutput,
  QuestionOutput,
  GenerateModuleSectionContentInput,
  HighlightContentInput,
  QuestionContentInput,
  SectionType,
  DefinitionContentInput,
} from '@/api/gql/graphql';
// import EvaluationFinal from './EvaluationFinal/EvaluationFinal';
import moduleIcon from '@/assets/icons/module.svg';
import { Button } from '@/components/Common';
import { ContentType, GeneratorModuleItem } from './types';
import { ContentGenerationOptions } from './ContentGenerationOptions';
import evaluationIcon from '@/assets/icons/evaluation.svg';

export const ContentGenerator = () => {
  const params = useParams();
  const { showToast } = useToast();
  const { setIsSidebarOpen } = useGlobalContextStore();
  const [selectedItemId, setSelectedItemId] = useState('');
  const [selectedModuleId, setSelectedModuleId] = useState('');
  const [isRegenerateModalOpen, setIsRegenerateModalOpen] = useState(false);
  const [shooseGeneratedContent, setShooseGeneratedContent] = useState(true);
  const { modules, isPending, handleRegenerateContent, noContent } =
    useGenerator();
  const { mutate: saveGeneratedContent, isPending: isSaving } =
    useSaveGeneratedContent();

  useEffect(() => {
    setIsSidebarOpen(false);
  }, [setIsSidebarOpen]);

  const moduleItems = useMemo(() => {
    // const nonModuleItems = [
    //   { _id: 'eval', name: 'Evaluation', type: GeneratorModuleItem.Evaluation },
    // ];

    const moduleEntries = modules
      ? modules.map((module: GenerateSectionOutput) => {
          return {
            _id: module.section._id || '',
            name: module.section.title || '',
            type: GeneratorModuleItem.Module,
            moduleData: module,
          };
        })
      : [];

    return [...moduleEntries];
  }, [modules]);

  const handleItemClick = (item: {
    _id: string;
    name: string;
    type: string;
  }) => {
    setSelectedItemId(item.type);
    if (item.type === GeneratorModuleItem.Module) {
      setSelectedModuleId(item._id);
    }
  };

  const convertModulesToGenerateModuleSectionContentInput =
    (): GenerateModuleSectionContentInput[] => {
      if (!modules || !modules.length) return [];

      return modules.map((module: GenerateSectionOutput) => ({
        highlights: module.highlights.map(
          (highlight: HighlightOutput): HighlightContentInput => {
            return {
              _id: highlight._id,
              term: highlight.term,
              slug: highlight.term,
              definition: {
                html: highlight.definition.html,
                text: highlight.definition.text || highlight.definition.html,
              } as DefinitionContentInput,
            };
          },
        ),
        quizz: module.quizz.map(
          (quiz: QuestionOutput): QuestionContentInput => ({
            name: quiz.name,
            type: quiz.type,
            responses:
              quiz.responses?.map((response) => ({
                isRight: response.isRight,
                key: response.key || '',
                response: response.response || '',
              })) || [],
          }),
        ),
        section: {
          _id: module.section._id,
          title: module.section.title,
          content:
            module.section.content?.map((content) => ({
              html: content.html,
              text: content.text || content.html,
              type: content.type || SectionType.Main,
              label: content.label || '',
            })) || [],
          lessonsId: params?.lessonId ?? '',
          position: module.section.position || 0,
        },
      }));
    };

  const handleSaveGeneratedContent = () => {
    try {
      const sectionsData = convertModulesToGenerateModuleSectionContentInput();

      saveGeneratedContent(
        {
          lessonId: params?.lessonId ?? '',
          module: {
            sections: sectionsData,
          },
        },
        {
          onSuccess: () => {
            showToast('Content saved successfully', 'success');
          },
          onError: (error) => {
            console.error('Error saving content:', error);
            showToast(
              'Failed to save content: ' + (error?.message || 'Unknown error'),
              'error',
            );
          },
        },
      );
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      console.error('Error preparing data for save:', error);
      showToast('Failed to prepare data for saving: ' + errorMessage, 'error');
    }
  };

  const handleOpenRegenerateModal = (content: string) => {
    switch (content) {
      case ContentType.Module:
        setSelectedItemId(GeneratorModuleItem.Module);
        setShooseGeneratedContent(false);
        setIsRegenerateModalOpen(true);
        break;
      case ContentType.Summary:
        setSelectedItemId(GeneratorModuleItem.Summary);
        setShooseGeneratedContent(false);
        break;
      case ContentType.Audio:
        setSelectedItemId(GeneratorModuleItem.Audio);
        setShooseGeneratedContent(false);
        break;
      case ContentType.Feedback:
        setSelectedItemId(GeneratorModuleItem.Feedback);
        setShooseGeneratedContent(false);
        break;
    }
  };

  const handleConfirmRegenerate = () => {
    handleRegenerateContent();
    setIsRegenerateModalOpen(false);
  };

  const handleOpenShooseGeneratedContent = () => {
    setShooseGeneratedContent(true);
  };

  return (
    <>
      <Header userName="Mas Aly" role="Admin" title="Gestion des leçons" />
      {isPending ? (
        <div className="min-h-[200px] flex items-center justify-center">
          <AiLoader icon={moduleIcon} name="des modules" />
        </div>
      ) : (
        <div className="grid grid-cols-[20%_auto]">
          <div className="gap-10 relative mr-[50px] overflow-hidden">
            <div className="flex flex-col h-[calc(100vh_-_110px)] overflow-hidden scrollbar-hide">
              {selectedItemId === GeneratorModuleItem.Summary ||
              selectedItemId === GeneratorModuleItem.Audio ||
              selectedItemId === GeneratorModuleItem.Feedback ? (
                ' '
              ) : (
                <div>
                  {modules?.length > 0 ? (
                    <TopicsList
                      optionalBtn={false}
                      optionalBtnTitle="Regénérer"
                      isLoading={isSaving}
                      showBtn={true}
                      items={moduleItems}
                      onItemClick={handleItemClick}
                      selectedItemId={selectedItemId}
                      btnTitle="Enregistrer"
                      onAddClick={handleSaveGeneratedContent}
                    />
                  ) : (
                    ''
                  )}
                </div>
              )}

              <Button
                title="Générer"
                className="w-[calc(100%-50px)] !bg-[#1E1700] text-primary mx-auto mt-2"
                onClick={handleOpenShooseGeneratedContent}
              />

              <Divider
                layout="vertical"
                className="absolute -right-5 bg-white w-[1px] h-[calc(100vh_-_110px)]"
              />
            </div>
          </div>
          <div className="overflow-y-auto scrollbar-hide">
            {selectedItemId === GeneratorModuleItem.Module ? (
              <div>
                {noContent ? (
                  <div className="flex-col items-center justify-center h-full w-full mt-32">
                    <img
                      src={evaluationIcon}
                      alt="evaluation"
                      className="mx-auto"
                    />
                    <h1 className="text-2xl font-normal  text-center text-[#8080808C] my-16 mx-auto">
                      Aucun contenu généré pour cette leçon.
                    </h1>
                  </div>
                ) : (
                  <GeneratedModule moduleId={selectedModuleId} />
                )}
              </div>
            ) : null}
            {selectedItemId === GeneratorModuleItem.Summary ? (
              <Summary />
            ) : null}
            {selectedItemId === GeneratorModuleItem.Audio ? (
              <AudioPage />
            ) : null}
            {/* {selectedItemId === GeneratorModuleItem.Evaluation ? (
              <EvaluationFinal />
            ) : null} */}
            {selectedItemId === GeneratorModuleItem.Feedback ? (
              <FeedbackModal
                isOpen={selectedItemId === GeneratorModuleItem.Feedback}
                onClose={() => setSelectedItemId(GeneratorModuleItem.Module)}
              />
            ) : null}
          </div>
        </div>
      )}

      <AlertModal
        isOpen={isRegenerateModalOpen}
        onClose={() => setIsRegenerateModalOpen(false)}
        title="Confirmation de régénération"
        message="Attention les anciens données (modules, highilights, quiz, évaluation) seront écrasées'"
        buttonTitle={isPending ? 'Régénération en cours...' : 'Régénérer'}
        buttonCancelTitle="Annuler"
        handleConfirm={handleConfirmRegenerate}
      />
      <ContentGenerationOptions
        isOpen={shooseGeneratedContent}
        onClose={() => setShooseGeneratedContent(false)}
        onOptionSelect={handleOpenRegenerateModal}
      />
    </>
  );
};
